import { forwardRef, useImperativeHandle, useMemo } from 'react'
import { TextInput } from 'react-native'

import {
  FormInput,
  HStack,
  NumberInput,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  type FormInputProps,
  type InputFieldProps,
  type InputProps,
  type SliderFilledTrackProps,
  type SliderProps,
  type SliderThumbProps,
  type SliderTrackProps
} from '@/components'
import { useFocusableRef } from '@/hooks'

export interface SliderInputProps extends Omit<FormInputProps, 'children' | 'inlineLabel'> {
  displayValue?: string
  inputFieldProps?: Omit<InputFieldProps, 'keyboardType' | 'maxLength' | 'onChangeText' | 'value'>
  inputProps?: InputProps
  onBlur?: () => void
  onChange: (value: number) => void
  onChangeEnd?: (value: number) => void
  sliderProps?: Omit<SliderProps, 'onValueChange' | 'value'>
  sliderFilledTrackProps?: SliderFilledTrackProps
  sliderTrackProps?: SliderTrackProps
  sliderThumbProps?: SliderThumbProps
  value?: number | null | undefined
  withTextInput?: boolean
}

export type SliderInputRef = {
  focus: () => void
}

export const SliderInput = forwardRef<SliderInputRef, SliderInputProps>(
  (
    {
      accessibilityLabel: _accessibilityLabel,
      displayValue,
      helper,
      inputFieldProps,
      inputProps,
      isDisabled,
      isRequired,
      label,
      onBlur,
      onChange,
      onChangeEnd,
      sliderProps,
      sliderFilledTrackProps,
      sliderTrackProps,
      sliderThumbProps,
      value: rawValue,
      withTextInput,
      ...props
    },
    ref
  ) => {
    const [inputRef, focusInput] = useFocusableRef<TextInput>()
    const [sliderRef, focusSlider] = useFocusableRef()
    const accessibilityLabel = useMemo(
      () => _accessibilityLabel ?? `${label}${isRequired ? '. Required' : ''}${helper ? `. ${helper}` : ''}`,
      [_accessibilityLabel, label, isRequired, helper]
    )
    const value = rawValue ?? 0
    const slider = (
      <Slider
        accessible={!withTextInput}
        accessibilityLabel={accessibilityLabel}
        accessibilityRole='adjustable'
        accessibilityState={{
          disabled: isDisabled
        }}
        accessibilityValue={{
          max: sliderProps?.maxValue,
          min: sliderProps?.minValue,
          now: value
        }}
        accessibilityElementsHidden
        className='mx-2 flex-1'
        importantForAccessibility={withTextInput ? 'no-hide-descendants' : 'auto'}
        maxValue={sliderProps?.maxValue}
        minValue={sliderProps?.minValue}
        onChange={onChange}
        onChangeEnd={onChangeEnd}
        role='slider'
        value={value}
        tabIndex={withTextInput ? -1 : 0}
        {...sliderProps}
      >
        <SliderTrack {...sliderTrackProps}>
          <SliderFilledTrack
            className='bg-brand-orange-500 data-[focus=true]:bg-brand-orange-500 data-[hover=true]:bg-brand-orange-500 data-[active=true]:bg-brand-orange-500'
            {...sliderFilledTrackProps}
          />
        </SliderTrack>
        <SliderThumb
          // @ts-expect-error stupid gluestack types
          ref={sliderRef}
          {...sliderThumbProps}
        />
      </Slider>
    )

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          if (withTextInput) {
            focusInput()
          } else {
            focusSlider()
          }
        }
      }),
      [focusInput, focusSlider, withTextInput]
    )

    return (
      <FormInput
        helper={helper}
        isDisabled={isDisabled}
        isRequired={isRequired}
        label={label}
        {...props}
      >
        {withTextInput ? (
          <HStack className='items-center justify-between gap-1'>
            {slider}
            <NumberInput
              accessibilityLabel={accessibilityLabel}
              className='flex-row'
              displayValue={displayValue}
              inputProps={inputProps}
              inputFieldProps={inputFieldProps}
              maxValue={sliderProps?.maxValue}
              minValue={sliderProps?.minValue}
              onBlur={onBlur}
              onChange={onChangeEnd || onChange}
              ref={inputRef}
              value={value}
            />
          </HStack>
        ) : (
          slider
        )}
      </FormInput>
    )
  }
)

SliderInput.displayName = 'SliderInput'
